import {
  AppBarComponent,
  authActions,
  selectAuth,
  setSidenavIsOpen,
} from '@aa/angular/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  firstValueFrom,
  map,
  Observable,
} from 'rxjs';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { isInStandaloneMode } from '@aa/ts/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { StaffAppState } from '../../state/index.reducers';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import '@khmyznikov/pwa-install';
import { USER_ROLE } from '@aa/nest/auth/objects';

const unprotectedRoutes: string[] = [
  '/login',
  '/forgot-password',
  '/reset-password/[0-9]+/.+',
];

const hideLayoutRoutes: string[] = [
  '/login',
  '/forgot-password',
  '/reset-password/[0-9]+/.+',
];

interface SidenavItem {
  key: string;
  label: string;
  icon?: string;
  routerPath?: string;
  children?: SidenavItem[];

  roles?: USER_ROLE[];
}

@Component({
  selector: 'aa-staff-app-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    AppBarComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './staff-app-layout.component.html',
  styleUrl: './staff-app-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StaffAppLayoutComponent {
  @ViewChild('pwaInstallPrompt') pwaInstallPrompt!: ElementRef;

  sidenavIsOpen$: Observable<boolean | undefined>;
  hideLayout$ = new BehaviorSubject(false);
  sidenavItems: SidenavItem[] = [
    // {
    //   key: 'home',
    //   label: 'Home',
    //   icon: 'home',
    //   routerPath: '/',
    // },
    {
      key: 'orders',
      label: 'Orders',
      icon: 'view_list',
      routerPath: '/orders',
    },
    {
      key: 'meetings',
      label: 'Meetings',
      icon: 'event_available',
      routerPath: '/meetings',
    },
    {
      key: 'customers',
      label: 'Customers',
      icon: 'face',
      routerPath: '/customers',
    },
    {
      key: 'configurables',
      label: 'Configuration',
      icon: 'manufacturing',
      children: [
        {
          key: 'offered-services',
          label: 'Offered Services',
          icon: 'conveyor_belt',
          routerPath: 'offered-services',

          // roles: [USER_ROLE.DEVELOPER],
        },
        {
          key: 'order-statuses',
          label: 'Order Statuses',
          icon: 'step',
          routerPath: '/order-statuses',
        },
        {
          key: 'order-sources',
          label: 'Order Sources',
          icon: 'move_location',
          routerPath: '/order-sources',
        },
        {
          key: 'meeting-types',
          label: 'Meeting Types',
          icon: 'event',
          routerPath: '/meeting-types',
        },
        {
          key: 'meeting-availability',
          label: 'Meeting Availability',
          icon: 'calendar_clock',
          routerPath: '/meeting-availability',
        },
        {
          key: 'order-card-attribute-types',
          label: 'Card Attribute Types',
          icon: 'description',
          routerPath: 'order-card-attribute-types',
          roles: [USER_ROLE.DEVELOPER],
        },
      ],
    },
    {
      key: 'admin',
      label: 'Admin',
      icon: 'admin_panel_settings',
      children: [
        {
          key: 'staff',
          label: 'Staff',
          icon: 'badge',
          routerPath: '/staff',
        },
        {
          key: 'actionLogs',
          label: 'Action Logs',
          icon: 'manage_search',
          routerPath: '/action-logs',
        },
        {
          key: 'adminJobs',
          label: 'Jobs',
          icon: 'dashboard_2_gear',
          routerPath: '/admin-jobs',
        },
      ],
    },
  ];
  expandedItems$ = new BehaviorSubject<string[]>([]);

  isSmall$: Observable<boolean>;

  shrankSidebar$ = new BehaviorSubject(false);

  constructor(
    private readonly store: Store<StaffAppState>,
    private readonly router: Router,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly snackBar: MatSnackBar,
  ) {
    this.sidenavIsOpen$ = this.store
      .select((s) => s.sidenavIsOpen)
      .pipe(takeUntilDestroyed());
    combineLatest([
      this.store.select((s) => selectAuth(s).user),
      this.router.events.pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      ),
    ]).subscribe(([user, routeEvent]) => {
      if (user) {
        this.sidenavItems = this.sidenavItems.reduce(
          (items, curr) =>
            !curr.roles ||
            curr.roles.find(
              (rId) =>
                user.userRoleAssignments?.find((ra) => ra.userRoleId == rId),
            )
              ? [
                  ...items,
                  {
                    ...curr,
                    children: curr.children?.filter(
                      (item) =>
                        !item.roles ||
                        item.roles.find(
                          (rId) =>
                            user.userRoleAssignments?.find(
                              (ra) => ra.userRoleId == rId,
                            ),
                        ),
                    ),
                  },
                ]
              : items,
          [] as SidenavItem[],
        );
      }

      const urlSlug = routeEvent.url
        .substring(routeEvent.url.indexOf('/'))
        .split('?')?.[0];

      if (
        hideLayoutRoutes.find((regex) => new RegExp(`^${regex}$`).test(urlSlug))
      ) {
        this.hideLayout$.next(true);
      } else {
        this.hideLayout$.next(false);
      }

      if (
        !unprotectedRoutes.find((regex) =>
          new RegExp(`^${regex}$`).test(urlSlug),
        ) &&
        !user
      ) {
        this.store.dispatch(
          authActions.setOriginalPath({ path: routeEvent.url }),
        );
        this.router.navigate(['login']);
      }
    });

    this.isSmall$ = breakpointObserver.observe([Breakpoints.XSmall]).pipe(
      takeUntilDestroyed(),
      map((breakpoint) => breakpoint.matches),
    );
    this.isSmall$.subscribe(async (isSmall) => {
      const shrankSidebar = await firstValueFrom(this.shrankSidebar$);
      if (isSmall && !shrankSidebar) {
        this.store.dispatch(setSidenavIsOpen({ open: false }));
        this.shrankSidebar$.next(true);
      }
    });
  }

  ngAfterViewInit() {
    const initiallyLoaded = localStorage.getItem('initiallyLoaded');
    if (initiallyLoaded != 'true') {
      // this.helpTooltip.show();
      this.showInstallPrompt();
      localStorage.setItem('initiallyLoaded', 'true');
    }
  }

  showInstallPrompt() {
    if (!isInStandaloneMode())
      (this.pwaInstallPrompt?.nativeElement as any).showDialog();
  }

  async toggleExpandedItem(sidenavItem: SidenavItem) {
    if (!sidenavItem.children) return;
    const expandedItems = await firstValueFrom(this.expandedItems$);
    if (expandedItems.includes(sidenavItem.key))
      this.expandedItems$.next([
        ...expandedItems.filter((i) => i != sidenavItem.key),
      ]);
    else this.expandedItems$.next([...expandedItems, sidenavItem.key]);
  }
}
